import { AssinaturaType } from '_metronic/helpers'
import { PrescricaoView } from './_model'
import {
  generateTermo,
  getPrescricaoById,
  updateGenerateAPAC,
  updateGenerateLME,
} from './_requests'

/** LME e APAC só podem ser gerados com a receita assinada: logo em seguida, ou depois. */
export type QuandoGerarSus = 'apos_assinar' | 'depois'
export type PreferenciasSus = { lme: QuandoGerarSus; apac: QuandoGerarSus }

/** Assinada, com medicamento de LME, e o LME ainda não foi gerado. */
export const precisaDeLme = (prescricao: PrescricaoView) =>
  !!prescricao.prescricaoLme && !prescricao.lme

/**
 * Com LME e sem nenhum documento anexado. O backend não diz de qual modelo veio cada documento;
 * neste fluxo o único documento da receita de LME é o termo, então "nenhum" é "termo pendente".
 */
export const precisaDeTermo = (prescricao: PrescricaoView) =>
  !!prescricao.prescricaoLme && !prescricao.documentos?.length

/** Assinada, com exame de APAC, e a APAC ainda não foi gerada. */
export const precisaDeApac = (prescricao: PrescricaoView) =>
  !!prescricao.prescricaoApac && !prescricao.apac

/** Meses do formulário LME. */
export const MESES_LME = 6

/** Medicamentos de LME de uma prescrição, pelo id do medicamento prescrito (é o que o backend espera). */
export const medicamentosDeLme = (prescricao: PrescricaoView) =>
  (prescricao.medicamentos ?? []).filter((prescrito) => prescrito.medicamento?.lme)

/** Exames de APAC de uma prescrição, pelo id do exame prescrito (é o que o backend espera). */
export const examesDeApac = (prescricao: PrescricaoView) =>
  (prescricao.exames ?? []).filter((exame) => exame.exame?.apac)

export type DadosLme = {
  cid: number
  anamnese: string
  temTratamento: boolean
  tratamentoPrevio?: string
  /** Modelo do termo vinculado ao CID; sem ele, só o LME é gerado. */
  modeloTermo?: number
  /** Meses preenchidos (a partir do 1º, sem pular) por medicamento prescrito. */
  quantidades: Record<number, number[]>
}

export type DadosApac = {
  cid: number
  anamnese: string
  /** Quantidade por exame prescrito (`ExamePrescritoList.id`). */
  quantidades: Record<number, number>
}

/**
 * Gera (e assina) o LME de cada prescrição, com os meses de cada medicamento, e, se escolhido, o
 * termo vinculado. O backend só aceita depois da receita assinada.
 */
export const gerarLme = async (
  prescricoes: PrescricaoView[],
  dados: DadosLme,
  tipo: AssinaturaType
) => {
  for (const prescricao of prescricoes) {
    // Idempotente: numa nova tentativa, o que já saiu no backend não é gerado de novo. LME e
    // termo são conferidos cada um: se o termo falhou depois do LME, a nova tentativa gera só ele.
    const atual = await getPrescricaoById(prescricao.id)
    if (precisaDeLme(atual)) {
      await updateGenerateLME({
        prescricao: prescricao.id,
        dadosPaciente: null,
        cid: dados.cid,
        anamnese: dados.anamnese,
        temTratamento: dados.temTratamento,
        tratamentoPrevio: dados.temTratamento ? dados.tratamentoPrevio : undefined,
        quantidadeLme: medicamentosDeLme(prescricao).map((prescrito) => ({
          id: prescrito.id,
          quantidade: dados.quantidades[prescrito.id] ?? [],
        })),
        tipo,
      })
    }
    if (dados.modeloTermo && precisaDeTermo(atual)) {
      await generateTermo({
        prescricao: prescricao.id,
        dadosPaciente: null,
        modelo: dados.modeloTermo,
        tipo,
      })
    }
  }
}

/** Gera (e assina) a APAC de cada prescrição, com a quantidade de cada exame. */
export const gerarApac = async (
  prescricoes: PrescricaoView[],
  dados: DadosApac,
  tipo: AssinaturaType
) => {
  for (const prescricao of prescricoes) {
    if (!precisaDeApac(await getPrescricaoById(prescricao.id))) continue
    await updateGenerateAPAC({
      prescricao: prescricao.id,
      dadosPaciente: null,
      cid: dados.cid,
      anamnese: dados.anamnese,
      quantidadeApac: examesDeApac(prescricao).map((exame) => ({
        id: exame.id,
        quantidade: dados.quantidades[exame.id],
      })),
      tipo,
    })
  }
}
